import { useEffect, FC } from "react";

import { Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { DateTime } from "luxon";

import Input from "@/common/components/form/Input";
import { phoneRule } from "@/common/formValidation";

import { ITenant } from "../../reducers";

interface ServiceOrderingPropertyFormProps {
  type: "outgoing" | "incoming" | "current";
}

const ServiceOrderingTenantForm: FC<ServiceOrderingPropertyFormProps> = ({
  type,
}) => {
  const { control, watch, setValue } = useFormContext();
  const name = `${type}Tenants`;
  const isOutgoingType = type === "outgoing";
  const label = () => {
    switch (type) {
      case "outgoing":
        return "sortant";
      case "incoming":
        return "entrant";
      default:
        return "";
    }
  };

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name,
  });
  // update tenant when dwelling change
  useEffect(() => {
    const subscription = watch((value, { name: typeName, type: typeWatch }) => {
      if (typeWatch === "change" && typeName === "dwelling") {
        const dwelling = value?.dwelling;
        if (dwelling?.[name] && dwelling?.[name]?.length > 0) {
          replace(
            dwelling?.[name]?.map((el: ITenant) => {
              return {
                idDwelling: el.id,
                lastname: el.person.lastname,
                firstname: el.person.firstname,
                email: el.person.email,
                phone: el.person.phone,
                rentalStartDate: el.rentalStartDate,
              };
            })
          );
        }
        if (isOutgoingType) {
          if (
            dwelling?.previousTenants &&
            dwelling.previousTenants.length > 0
          ) {
            setValue("previousTenant", {
              id: dwelling.previousTenants[0].id,
              lastname: dwelling.previousTenants[0].person.lastname,
              firstname: dwelling.previousTenants[0].person.firstname,
            });
          } else {
            setValue("previousTenant", { lastname: "", firstname: "" });
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <div className="serviceOrdering_tenant">
      <div className="serviceOrdering-subtitle">
        Information occupant {label()}
      </div>
      <div className="serviceOrdering-text">
        Indiquez les informations du/des occupant(s) pour la prise de
        rendez-vous
        {isOutgoingType && " de l’état des lieux"}
      </div>
      {fields.map((tenant, index) => (
        <div key={tenant.id} className="d-flex flex-column gap-3">
          <div className="d-flex justify-content-between">
            <span className="serviceOrdering-subtitle">
              Occupant {index + 1} :{" "}
            </span>
            {fields.length > 1 && (
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => remove(index)}
              >
                Supprimer
              </Button>
            )}
          </div>
          <div className="serviceOrdering_tenant_layout-infos">
            <Input
              label="Prénom"
              control={control}
              defaultValue=""
              name={`${name}.${index}.firstname` as any}
              rules={{ required: "Veuillez renseigner un prénom" }}
            />
            <Input
              label="Nom"
              control={control}
              defaultValue=""
              name={`${name}.${index}.lastname` as any}
              rules={{ required: "Veuillez renseigner un nom" }}
            />
            <Input
              label="Numéro de téléphone"
              control={control}
              defaultValue=""
              name={`${name}.${index}.phone` as any}
              rules={{
                required: "Veuillez renseigner un numéro de téléphone",
                ...phoneRule,
              }}
            />
            <Input
              label="Adresse email"
              control={control}
              defaultValue=""
              name={`${name}.${index}.email` as any}
              rules={{ required: "Veuillez renseigner un email" }}
            />
            {isOutgoingType && (
              <div className="form-input mb-3">
                <div className="form-input_label">Date d'entrée</div>
                <Controller
                  control={control}
                  name={`${name}.${index}.rentalStartDate`}
                  render={({ field }) => {
                    return (
                      <div>
                        <DatePicker
                          className={`w-100 form-input_field date-select`}
                          dateFormat="dd/MM/yyyy"
                          locale="fr"
                          onChange={(date) =>
                            field.onChange(DateTime.fromJSDate(date).toISO())
                          }
                          selected={
                            field.value ? new Date(field.value) : undefined
                          }
                        />
                      </div>
                    );
                  }}
                />
              </div>
            )}
          </div>
          <div className="border-bottom" />
        </div>
      ))}
      <div>
        <Button
          variant="outline-primary"
          onClick={() => {
            append({});
          }}
        >
          <i className="bi bi-plus-lg" />
          Ajouter un occupant {label()}
        </Button>
      </div>
      {isOutgoingType && (
        <div className="d-flex flex-column gap-2">
          <div className="border-bottom" />
          <span className="serviceOrdering-subtitle">Précédent occupant</span>
          <div className="serviceOrdering_tenant_layout-infos">
            <Input
              label="Nom du précédent occupant"
              control={control}
              defaultValue=""
              name="previousTenant.lastname"
              disabled={watch("previousTenant")?.id}
            />
            <Input
              label="Prénom du précédent occupant"
              control={control}
              defaultValue=""
              name="previousTenant.firstname"
              disabled={watch("previousTenant")?.id}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceOrderingTenantForm;
